import React, {
    KeyboardEvent,
    MouseEvent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { IconButton, TextField } from '@mui/material'
import { CheckIcon, CloseIcon, EditIcon } from '../components'
import { AnyInlineInputProps } from './AnyInlineInput.types'
import {
    InputValidator,
    NUMBER_VALIDATOR,
    RequiredValidator,
} from '../validators'

function AnyInlineInput({
    type = 'text',
    name,
    initialValue,
    disabled,
    required,
    validators = [],
    onUpdated,
    onEditing,
}: AnyInlineInputProps) {
    const [editable, setEditable] = useState(false)
    const [value, setValue] = useState<string>(
        initialValue === undefined || initialValue === null
            ? ''
            : `${initialValue}`
    )
    const [error, setError] = useState<string>()
    const ref = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (onEditing) {
            onEditing(name, editable)
        }
    }, [name, editable, onEditing])

    const allValidators = useMemo<InputValidator[]>(() => {
        let allValidators = [...validators]
        if (required) {
            allValidators = [new RequiredValidator(type), ...validators]
        }

        switch (type) {
            case 'number':
                return [NUMBER_VALIDATOR, ...allValidators]
            default:
                return allValidators
        }
    }, [required, type, validators])

    const onUpdate = useCallback(
        (value?: string) => {
            let valid = true
            let error: string | undefined
            const fixedValue = value
                ? type === 'number'
                    ? +value
                    : value
                : undefined
            for (const validator of allValidators) {
                if (!validator.isValid(fixedValue)) {
                    valid = false
                    error = validator.getMessage()
                    break
                }
            }

            if (valid) {
                setEditable(false)
                setError(undefined)
                onUpdated(name, value)
            } else {
                setError(error)
            }
        },
        [onUpdated, allValidators, name, type]
    )

    const onCancel = useCallback(() => {
        setEditable(false)
        setValue(
            initialValue === undefined || initialValue === null
                ? ''
                : `${initialValue}`
        )
        setError(undefined)
    }, [initialValue])

    const onKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            e.stopPropagation()

            if (!editable) {
                e.preventDefault()
            } else if (e.key === 'Enter') {
                e.preventDefault()
                ref.current?.focus()
                onUpdate(ref.current?.value)
            } else if (e.key === 'Escape') {
                onCancel()
            }
        },
        [editable, onUpdate, onCancel]
    )

    const onCancelled = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()
            onCancel()
        },
        [onCancel]
    )

    const onEdit = useCallback((e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        setEditable(true)
        ref.current?.focus()
    }, [])

    const onAccept = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()
            onUpdate(value)
        },
        [onUpdate, value]
    )

    return (
        <TextField
            variant="standard"
            type={type}
            size="small"
            margin="dense"
            value={value}
            required={required}
            fullWidth
            onClick={disabled ? undefined : () => setEditable(true)}
            onChange={
                disabled ? undefined : (event) => setValue(event.target.value)
            }
            onKeyDown={disabled ? undefined : onKeyDown}
            inputProps={{
                ref: ref,
            }}
            InputProps={{
                disableUnderline: disabled,
                readOnly: disabled || !editable,
                startAdornment: disabled ? undefined : editable ? (
                    <IconButton size="small" onClick={onCancelled}>
                        <CloseIcon />
                    </IconButton>
                ) : (
                    <IconButton
                        size="small"
                        onClick={onEdit}
                        disabled={disabled}
                    >
                        <EditIcon />
                    </IconButton>
                ),
                endAdornment: (
                    <IconButton
                        size="small"
                        color="success"
                        onClick={onAccept}
                        disabled={!editable}
                        sx={editable ? undefined : { visibility: 'hidden' }}
                    >
                        <CheckIcon />
                    </IconButton>
                ),
            }}
            error={!!error && editable}
            helperText={editable ? error : undefined}
        />
    )
}

export default memo(AnyInlineInput)
